"use client"

import React, { useState, useMemo } from 'react'
import { useSearchParams, usePathname, useRouter } from 'next/navigation'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'
import { ChevronDown } from 'lucide-react'
import { useWorkScopes } from '@/features/work-orders/hooks/use-work-scopes'
import { workScopeStatusOptions } from '../constants/options'
import { getGoodwillOptionsForRegion } from '../lib/workscope-goodwill-filter'

function parseMultiParam(param: string | null): string[] {
  if (!param || !param.trim()) return []
  return param.split(',').map((s) => s.trim()).filter(Boolean)
}

function QuickMultiselect({
  selectedValues,
  onSelectionChange,
  options,
  placeholder,
  searchPlaceholder = 'Search...',
  triggerClassName,
}: {
  selectedValues: string[]
  onSelectionChange: (values: string[]) => void
  options: readonly { label: string; value: string }[]
  placeholder: string
  searchPlaceholder?: string
  triggerClassName?: string
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const filteredOptions = useMemo(
    () =>
      options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          option.value.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [options, searchTerm]
  )

  const selectedOptions = useMemo(
    () =>
      selectedValues
        .map((v) => options.find((o) => String(o.value) === String(v)))
        .filter(Boolean) as { label: string; value: string }[],
    [selectedValues, options]
  )

  const handleToggle = (value: string) => {
    const valStr = String(value)
    const newSelection = selectedValues.includes(valStr)
      ? selectedValues.filter((v) => v !== valStr)
      : [...selectedValues, valStr]
    onSelectionChange(newSelection)
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) setSearchTerm('')
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={isOpen}
          className={cn(
            'h-10 justify-between shrink-0 min-w-0 overflow-hidden',
            !selectedValues.length && 'text-muted-foreground',
            triggerClassName
          )}
        >
          {selectedValues.length > 0 ? (
            <span className="truncate min-w-0 max-w-[90px] sm:max-w-[140px]">
              {selectedValues.length === 1 && selectedOptions[0]
                ? selectedOptions[0].label
                : `${selectedValues.length} selected`}
            </span>
          ) : (
            <span className="truncate min-w-0">{placeholder}</span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[180px] p-0" align="start">
        <div className="p-2">
          <input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div
            className="max-h-60 overflow-y-auto mt-2"
            onWheel={(e) => {
              const el = e.currentTarget
              el.scrollTop += e.deltaY
              e.preventDefault()
            }}
          >
            {filteredOptions.length > 0 ? (
              <div className="space-y-1">
                {filteredOptions.map((option) => (
                  <div
                    key={option.value}
                    className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm cursor-pointer"
                    onClick={() => handleToggle(option.value)}
                  >
                    <Checkbox
                      checked={selectedValues.includes(String(option.value))}
                      className="rounded-[5px] border-primary"
                    />
                    <span className="text-sm text-foreground">{option.label}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-muted-foreground">No options found</div>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function HomeownersQuickWorkscopeStatusFilters({
  show = 'all',
  className,
}: {
  show?: 'all' | 'scopeGoodwill' | 'status'
  className?: string
}) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()
  const { workScopeOptions } = useWorkScopes()

  const workscopeSelected = useMemo(
    () => parseMultiParam(searchParams.get('workscope')),
    [searchParams]
  )
  const workscopeStatusSelected = useMemo(
    () => parseMultiParam(searchParams.get('workscopeStatus')),
    [searchParams]
  )
  const workscopeGoodwillSelected = useMemo(
    () => parseMultiParam(searchParams.get('workscopeGoodwill')),
    [searchParams]
  )
  const workscopeGoodwillStatusSelected = useMemo(
    () => parseMultiParam(searchParams.get('workscopeGoodwillStatus')),
    [searchParams]
  )

  // All Status drives both workscope and workscope goodwill status; display whichever is set so bar stays in sync with URL
  const allStatusDisplayValues = useMemo(
    () => (workscopeStatusSelected.length ? workscopeStatusSelected : workscopeGoodwillStatusSelected),
    [workscopeStatusSelected, workscopeGoodwillStatusSelected]
  )

  const setParams = (updates: { workscope?: string; workscopeStatus?: string; workscopeGoodwill?: string; workscopeGoodwillStatus?: string }) => {
    const params = new URLSearchParams(searchParams.toString())
    if (updates.workscope !== undefined) {
      if (updates.workscope) params.set('workscope', updates.workscope)
      else params.delete('workscope')
    }
    if (updates.workscopeStatus !== undefined) {
      if (updates.workscopeStatus) params.set('workscopeStatus', updates.workscopeStatus)
      else params.delete('workscopeStatus')
    }
    if (updates.workscopeGoodwill !== undefined) {
      if (updates.workscopeGoodwill) params.set('workscopeGoodwill', updates.workscopeGoodwill)
      else params.delete('workscopeGoodwill')
    }
    if (updates.workscopeGoodwillStatus !== undefined) {
      if (updates.workscopeGoodwillStatus) params.set('workscopeGoodwillStatus', updates.workscopeGoodwillStatus)
      else params.delete('workscopeGoodwillStatus')
    }
    params.set('page', '1')
    router.push(`${pathname}?${params.toString()}`)
  }

  const onWorkscopeChange = (values: string[]) => {
    setParams({ workscope: values.length ? values.join(',') : '' })
  }

  const onWorkscopeStatusChange = (values: string[]) => {
    const statusStr = values.length ? values.join(',') : ''
    setParams({ workscopeStatus: statusStr, workscopeGoodwillStatus: statusStr })
  }

  const onWorkscopeGoodwillChange = (values: string[]) => {
    setParams({ workscopeGoodwill: values.length ? values.join(',') : '' })
  }

  const statusOptions = useMemo(
    () => workScopeStatusOptions.map((o) => ({ label: o.label, value: o.value })),
    []
  )

  const goodwillOptions = useMemo(
    () => getGoodwillOptionsForRegion(workScopeOptions.map((o) => o.value)).map((o) => ({ label: o.label, value: o.value })),
    [workScopeOptions]
  )

  return (
    <div className={cn("flex flex-wrap items-center gap-2 min-w-0", className)}>
      {(show === 'all' || show === 'scopeGoodwill') && (
        <>
          <QuickMultiselect
            selectedValues={workscopeSelected}
            onSelectionChange={onWorkscopeChange}
            options={workScopeOptions}
            placeholder="All Workscopes"
            searchPlaceholder="Search..."
            triggerClassName="min-w-0 max-w-[48%] sm:max-w-none sm:min-w-[160px]"
          />
          <QuickMultiselect
            selectedValues={workscopeGoodwillSelected}
            onSelectionChange={onWorkscopeGoodwillChange}
            options={goodwillOptions}
            placeholder="Goodwill Workscope"
            searchPlaceholder="Search..."
            triggerClassName="min-w-0 max-w-[48%] sm:max-w-none sm:min-w-[160px]"
          />
        </>
      )}
      {(show === 'all' || show === 'status') && (
        <QuickMultiselect
          selectedValues={allStatusDisplayValues}
          onSelectionChange={onWorkscopeStatusChange}
          options={statusOptions}
          placeholder="Status"
          searchPlaceholder="Search..."
          triggerClassName="min-w-[80px] max-w-[120px] sm:max-w-none sm:min-w-[140px]"
        />
      )}
    </div>
  )
}

export default HomeownersQuickWorkscopeStatusFilters
